import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { IDataKeys, IPhoneNumber, IPlace, IStatus } from "shared/types";
import { formatDateTime, getCountry, ICountry } from "shared/utils";
import {
  clearDataByKey,
  dispatchGetUserAddresses,
  dispatchGetUserDrivingPermits,
  dispatchGetUserEmergencyContacts,
  dispatchGetUserHomeBases,
  dispatchGetUserLoyaltyPrograms,
  dispatchGetUserPassports,
  dispatchGetUserPhoneNumbers,
  dispatchGetUserSocialSecurityForms,
  dispatchGetUserTravelVisas,
  dispatchGetUserVehicles,
} from "Bands/state";
import { IActionDropdownItem } from "shared/components";
import { copy } from "shared/assets";
import { useUserEntityModals, useSortedContactDetails, useSortedUserDocuments } from "Bands/utils";
import {
  IBandUserBodyPreferencesProps,
  IBandUserFlightPreferencesProps,
  IBandUserHotelPreferencesProps,
  IUserDrivingPermit,
  IUserEmergencyContact,
  IUserHomeBase,
  IUserLoyaltyProgram,
  IUserPassport,
  IUserSocialSecurityForm,
  IUserTravelVisa,
  IUserVehicle,
} from "Bands/types";
import { IUser } from "Auth/types";

interface GetItemActionsProps {
  handleEdit: () => void;
  handleDelete: () => void;
}

export enum UserEntityModalType {
  address = "address",
  phoneNumber = "phoneNumber",
  emergencyContact = "emergencyContact",
  homeBase = "homeBase",
  passport = "passport",
  travelVisa = "travelVisa",
  socialSecurityForm = "socialSecurityForm",
  drivingPermit = "drivingPermit",
  vehicle = "vehicle",
  loyaltyProgram = "loyaltyProgram",
}

export interface UserEntityModalProps {
  id: string;
  type: UserEntityModalType;
}

interface FullUserProfileContextState {
  userName: string;
  email?: string;
  formattedBirthDate?: string | null;
  nationalityCountry?: ICountry | null;
  addresses?: IPlace[];
  phoneNumbers?: IPhoneNumber[];
  emergencyContacts?: IUserEmergencyContact[];
  homeBases?: IUserHomeBase[];
  passports?: IUserPassport[];
  travelVisas?: IUserTravelVisa[];
  socialSecurityForms?: IUserSocialSecurityForm[];
  drivingPermits?: IUserDrivingPermit[];
  vehicles?: IUserVehicle[];
  loyaltyPrograms?: IUserLoyaltyProgram[];
  hasBodyPreferences: boolean;
  hasHotelPreferences: boolean;
  hasFlightPreferences: boolean;
  bodyPreferences?: IBandUserBodyPreferencesProps;
  hotelPreferences?: IBandUserHotelPreferencesProps;
  flightPreferences?: IBandUserFlightPreferencesProps;
  setShowCreateModal: (type: UserEntityModalType) => void;
  setShowEditModal: (props: UserEntityModalProps) => void;
  setShowDeleteModal: (props: UserEntityModalProps) => void;
  getItemActions: (props: GetItemActionsProps) => IActionDropdownItem[];
}

const initialState: FullUserProfileContextState = {
  userName: "",
  email: "",
  setShowCreateModal: () => {},
  setShowEditModal: () => {},
  setShowDeleteModal: () => {},
  getItemActions: () => [],
  hasBodyPreferences: false,
  hasHotelPreferences: false,
  hasFlightPreferences: false,
};

const FullUserProfileContext = createContext(initialState);

interface Props {
  user: IUser | null;
}

export const FullUserProfileProvider: React.FC<Props> = ({ user, children }) => {
  const { attributes: userAttributes } = user || {};

  const dispatch = useDispatch();

  useEffect(() => {
    if (!user?.id) return;

    dispatchGetUserAddresses(dispatch, user.id);
    dispatchGetUserPhoneNumbers(dispatch, user.id);
    dispatchGetUserEmergencyContacts(dispatch, user.id);
    dispatchGetUserHomeBases(dispatch, user.id);
    dispatchGetUserPassports(dispatch, user.id);
    dispatchGetUserTravelVisas(dispatch, user.id);
    dispatchGetUserSocialSecurityForms(dispatch, user.id);
    dispatchGetUserDrivingPermits(dispatch, user.id);
    dispatchGetUserVehicles(dispatch, user.id);
    dispatchGetUserLoyaltyPrograms(dispatch, user.id);

    // clear data on unmount
    return () => {
      dispatch(clearDataByKey(IDataKeys.userAddress));
      dispatch(clearDataByKey(IDataKeys.userPhoneNumber));
      dispatch(clearDataByKey(IDataKeys.userEmergencyContact));
      dispatch(clearDataByKey(IDataKeys.userHomeBase));
      dispatch(clearDataByKey(IDataKeys.userPassport));
      dispatch(clearDataByKey(IDataKeys.userTravelVisa));
      dispatch(clearDataByKey(IDataKeys.userSocialSecurityForm));
      dispatch(clearDataByKey(IDataKeys.userDrivingPermit));
      dispatch(clearDataByKey(IDataKeys.userVehicle));
      dispatch(clearDataByKey(IDataKeys.userLoyaltyProgram));
    };
  }, [user?.id, dispatch]);

  const { firstname, lastname, email, bodyPreferences, hotelPreferences, flightPreferences } =
    userAttributes || {};

  const userName = useMemo(() => `${firstname} ${lastname}`, [firstname, lastname]);

  const formattedBirthDate = useMemo(
    () =>
      userAttributes?.birthDate
        ? formatDateTime(userAttributes?.birthDate, { dateOnly: true })
        : null,
    [userAttributes?.birthDate]
  );

  const nationalityCountry = useMemo(
    () => (userAttributes?.nationality ? getCountry(userAttributes?.nationality) : null),
    [userAttributes?.nationality]
  );

  const { addresses, phoneNumbers, emergencyContacts, homeBases } = useSortedContactDetails();

  const { passports, travelVisas, socialSecurityForms, drivingPermits, vehicles, loyaltyPrograms } =
    useSortedUserDocuments();

  const getItemActions = useCallback(
    ({ handleEdit, handleDelete }: GetItemActionsProps): IActionDropdownItem[] => {
      return [
        {
          label: copy.actions.edit,
          handleClick: handleEdit,
        },
        {
          label: copy.actions.delete,
          handleClick: handleDelete,
          status: IStatus.error,
        },
      ];
    },
    []
  );

  const hasBodyPreferences = useMemo(() => {
    const { bodyPreferences } = userAttributes || {};

    return !!(
      bodyPreferences?.allergies ||
      bodyPreferences?.dietaryRequirements ||
      bodyPreferences?.otherImportantInformation ||
      bodyPreferences?.shirtSize ||
      bodyPreferences?.shoeSize ||
      bodyPreferences?.smoker !== null
    );
  }, [userAttributes]);

  const hasHotelPreferences = useMemo(() => {
    const { hotelPreferences } = userAttributes || {};

    return !!(
      hotelPreferences?.smokingRoom !== null ||
      hotelPreferences?.accessibleRoom !== null ||
      hotelPreferences?.roomLocation ||
      hotelPreferences?.elevatorProximity
    );
  }, [userAttributes]);

  const hasFlightPreferences = useMemo(() => {
    const { flightPreferences } = userAttributes || {};

    return !!flightPreferences?.seatPreference;
  }, [userAttributes]);

  const { setShowCreateModal, setShowEditModal, setShowDeleteModal, modals } =
    useUserEntityModals();

  return (
    <FullUserProfileContext.Provider
      value={{
        userName,
        email,
        formattedBirthDate,
        nationalityCountry,
        addresses,
        phoneNumbers,
        emergencyContacts,
        homeBases,
        passports,
        travelVisas,
        socialSecurityForms,
        drivingPermits,
        vehicles,
        loyaltyPrograms,
        getItemActions,
        setShowCreateModal,
        setShowEditModal,
        setShowDeleteModal,
        hasBodyPreferences,
        hasHotelPreferences,
        hasFlightPreferences,
        bodyPreferences,
        hotelPreferences,
        flightPreferences,
      }}
    >
      {children}
      {modals}
    </FullUserProfileContext.Provider>
  );
};

export const useFullUserProfileContext = () => useContext(FullUserProfileContext);
